import React, { ReactNode, useContext, useEffect, useState } from 'react';
import isEqual from 'lodash/isEqual';

import {
  addDeviceFilters,
  buildDeviceFilters,
} from '@totem/components/devices/devicesContainer/utilities';
import OsPatchReportContext from '@totem/components/osPatchReport/osPatchReportContext';
import { OSPatchEntry } from '@totem/components/osPatchReport/types';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { Params } from '@totem/types/common';
import {
  DeviceFilterOptions,
  DeviceFilters,
  DevicesConnectionInput,
} from '@totem/types/devices';
import { DEVICES_ENDPOINT } from '@totem/utilities/endpoints';
function didDeepValuesChange(
  compA: Record<string, any>,
  compB: Record<string, any>,
): boolean {
  for (const key in compA) {
    if (!isEqual(compA[key], compB[key])) {
      return true;
    }
  }
  return false;
}
type Props = {
  children?: ReactNode;
  defaultFilters?: DeviceFilters;
  staticFilters?: DeviceFilters;
  onRecordTotalChanged?: (total: number) => void;
};
const OSPatchReportContainer = ({
  children,
  staticFilters,
  defaultFilters,
  onRecordTotalChanged,
}: Props) => {
  const { userProfile, getApiToken } = useContext(UserProfileContext);
  const [refreshData, setRefreshData] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [data, setData] = useState<OSPatchEntry[]>([]);
  const [filteredData, setFilteredData] = useState<OSPatchEntry[]>([]);
  const [isFilterOptionsLoaded, setIsFilterOptionsLoaded] =
    useState<boolean>(false);
  const [filterOptions, setFilterOptions] = useState<DeviceFilterOptions>({
    type: [],
    vendor: [],
    models: [],
    operatingSystem: [],
    firmware: [],
    region: [],
    building: [],
  });
  const [input, updateInput] = useState<DevicesConnectionInput>({
    from: '',
    pageSize: 10,
    page: 1,
    sortField: 'device.displayName',
    sortDirection: '1',
    organizationId:
      typeof defaultFilters !== 'undefined' &&
      defaultFilters !== null &&
      typeof defaultFilters.organizationId !== 'undefined' &&
      defaultFilters.organizationId !== null
        ? defaultFilters.organizationId
        : [],
    regionId:
      typeof defaultFilters !== 'undefined' &&
      defaultFilters !== null &&
      typeof defaultFilters.regionId !== 'undefined' &&
      defaultFilters.regionId !== null
        ? defaultFilters.regionId
        : [],
    buildingId:
      typeof defaultFilters !== 'undefined' &&
      defaultFilters !== null &&
      typeof defaultFilters.buildingId !== 'undefined' &&
      defaultFilters.buildingId !== null
        ? defaultFilters.buildingId
        : [],
    controlSystemId:
      typeof defaultFilters !== 'undefined' &&
      defaultFilters !== null &&
      typeof defaultFilters.controlSystemId !== 'undefined' &&
      defaultFilters.controlSystemId !== null
        ? defaultFilters.controlSystemId
        : [],
    operatingSystem:
      typeof defaultFilters !== 'undefined' &&
      defaultFilters !== null &&
      typeof defaultFilters.operatingSystem !== 'undefined' &&
      defaultFilters.operatingSystem !== null
        ? defaultFilters.operatingSystem
        : [],
    firmware:
      typeof defaultFilters !== 'undefined' &&
      defaultFilters !== null &&
      typeof defaultFilters.firmware !== 'undefined' &&
      defaultFilters.firmware !== null
        ? defaultFilters.firmware
        : [],
    type:
      typeof defaultFilters !== 'undefined' &&
      defaultFilters !== null &&
      typeof defaultFilters.type !== 'undefined' &&
      defaultFilters.type !== null
        ? defaultFilters.type
        : [],
    vendor:
      typeof defaultFilters !== 'undefined' &&
      defaultFilters !== null &&
      typeof defaultFilters.vendor !== 'undefined' &&
      defaultFilters.vendor !== null
        ? defaultFilters.vendor
        : [],
    model:
      typeof defaultFilters !== 'undefined' &&
      defaultFilters !== null &&
      typeof defaultFilters.model !== 'undefined' &&
      defaultFilters.model !== null
        ? defaultFilters.model
        : [],
    searchTerm:
      typeof defaultFilters !== 'undefined' &&
      defaultFilters !== null &&
      typeof defaultFilters.searchTerm !== 'undefined' &&
      defaultFilters.searchTerm !== null
        ? defaultFilters.searchTerm
        : '',
    searchIpAddress:
      typeof defaultFilters !== 'undefined' &&
      defaultFilters !== null &&
      typeof defaultFilters.searchIpAddress !== 'undefined' &&
      defaultFilters.searchIpAddress !== null
        ? defaultFilters.searchIpAddress
        : '',
    isManaged:
      typeof defaultFilters !== 'undefined' &&
      defaultFilters !== null &&
      typeof defaultFilters.isManaged !== 'undefined' &&
      defaultFilters.isManaged !== null
        ? defaultFilters.isManaged
        : [],
    isMonitored:
      typeof defaultFilters !== 'undefined' &&
      defaultFilters !== null &&
      typeof defaultFilters.isMonitored !== 'undefined' &&
      defaultFilters.isMonitored !== null
        ? defaultFilters.isMonitored
        : [],
    noBackup:
      typeof defaultFilters !== 'undefined' &&
      defaultFilters !== null &&
      typeof defaultFilters.noBackup !== 'undefined' &&
      defaultFilters.noBackup !== null
        ? defaultFilters.noBackup
        : [],
    staleBackups:
      typeof defaultFilters !== 'undefined' &&
      defaultFilters !== null &&
      typeof defaultFilters.staleBackups !== 'undefined' &&
      defaultFilters.staleBackups !== null
        ? defaultFilters.staleBackups
        : [],
    noAntiVirus:
      typeof defaultFilters !== 'undefined' &&
      defaultFilters !== null &&
      typeof defaultFilters.noAntiVirus !== 'undefined' &&
      defaultFilters.noAntiVirus !== null
        ? defaultFilters.noAntiVirus
        : [],
    incompleteDeviceType:
      typeof defaultFilters !== 'undefined' &&
      defaultFilters !== null &&
      typeof defaultFilters.incompleteDeviceType !== 'undefined' &&
      defaultFilters.incompleteDeviceType !== null
        ? defaultFilters.incompleteDeviceType
        : [],
    operatingSystemPastEndOfSupport:
      typeof defaultFilters !== 'undefined' &&
      defaultFilters !== null &&
      typeof defaultFilters.operatingSystemPastEndOfSupport !== 'undefined' &&
      defaultFilters.operatingSystemPastEndOfSupport !== null
        ? defaultFilters.operatingSystemPastEndOfSupport
        : [],
    operatingSystemApproachingEndOfSupport:
      typeof defaultFilters !== 'undefined' &&
      defaultFilters !== null &&
      typeof defaultFilters.operatingSystemApproachingEndOfSupport !==
        'undefined' &&
      defaultFilters.operatingSystemApproachingEndOfSupport !== null
        ? defaultFilters.operatingSystemApproachingEndOfSupport
        : [],
    lowDiskSpace:
      typeof defaultFilters !== 'undefined' &&
      defaultFilters !== null &&
      typeof defaultFilters.lowDiskSpace !== 'undefined' &&
      defaultFilters.lowDiskSpace !== null
        ? defaultFilters.lowDiskSpace
        : [],
  });
  const setInput = (updated: Partial<DevicesConnectionInput>) => {
    const changed = didDeepValuesChange(updated, input);
    if (changed) {
      updateInput((prevInput) => {
        const newInput = { ...prevInput, ...updated };
        return newInput;
      });

      setRefreshData(true);
    }
  };
  useEffect(() => {
    setRefreshData(true);
  }, [userProfile.organization.id]);

  useEffect(() => {
    setFilteredData(data);
    if (typeof onRecordTotalChanged === 'function') {
      onRecordTotalChanged(data.length);
    }
  }, [data]);
  const buildParameters = () => {
    const params: Params = {
      paging: {
        page: input.page,
        pageSize: input.pageSize,
      },
      sort: {
        field: input.sortField,
        direction: +input.sortDirection,
      },
      filters: buildDeviceFilters(input),
    };
    params.filters = addDeviceFilters(params.filters, staticFilters);
    return params;
  };
  useEffect(() => {
    if (refreshData) {
      setRefreshData(false);

      setIsLoading(true);
      const params: Params = buildParameters();
      const uri = `${DEVICES_ENDPOINT}/osPatchReport`;
      fetch(uri, {
        method: 'POST',
        headers: new Headers({
          Authorization: `Bearer ${getApiToken()}`,
        }),
        body: JSON.stringify(params),
      })
        .then((res) => res.json())
        .then((result: OSPatchEntry[]) => {
          setData(result);
        })
        .then(() => {
          setIsLoading(false);
        });
    }
  }, [refreshData, getApiToken]);

  useEffect(() => {
    setIsFilterOptionsLoaded(false);
  }, [userProfile.organization.id]);

  useEffect(() => {
    if (!isFilterOptionsLoaded) {
      const deviceFiltersUrl = `${DEVICES_ENDPOINT}/filters`;
      fetch(`${deviceFiltersUrl}`, {
        method: 'GET',
        headers: new Headers({
          Authorization: `Bearer ${getApiToken()}`,
        }),
      })
        .then((res) => res.json())
        .then((result: DeviceFilterOptions) => {
          setFilterOptions(result);
        })
        .then(() => {
          setIsFilterOptionsLoaded(true);
        });
    }
  }, [isFilterOptionsLoaded, getApiToken]);
  return (
    <OsPatchReportContext.Provider
      value={{
        loading: isLoading,
        filteredData,
        input,
        setInput,
        filterOptions,
      }}
    >
      {children}
    </OsPatchReportContext.Provider>
  );
};
export default OSPatchReportContainer;
